// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Mutation hook for Pull: copy the cached SFDC value for one field
 * into the Hardfin entity. Invalidates both the discrepancies query
 * and the entity's detail query so the blue-dot indicator and the
 * Hardfin field values refresh in lockstep.
 */
import { pullSFDCDriftField } from '@/sfdc/api/drift';
import {
  SFDCDriftEntityType,
  SFDCDriftResolutionResponse,
} from '@/sfdc/api/types';
import {
  sfdcDriftDiffQueryKey,
  sfdcDriftEntityDetailQueryKey,
} from '@/sfdc/hooks/query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface Variables {
  fieldName: string;
}

export function usePullSFDCDriftField(
  entityType: SFDCDriftEntityType,
  entityKey: string
) {
  const queryClient = useQueryClient();

  return useMutation<SFDCDriftResolutionResponse, AxiosError, Variables>({
    mutationFn: ({ fieldName }) =>
      pullSFDCDriftField(entityType, entityKey, fieldName),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: sfdcDriftDiffQueryKey(entityType, entityKey),
      });
      void queryClient.invalidateQueries({
        queryKey: sfdcDriftEntityDetailQueryKey(entityType, entityKey),
      });
    },
  });
}
